import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Crown, X } from '@phosphor-icons/react';
import type { TaskDetail, Worker } from '@mybusinessmate/schema';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { OVER_CAPACITY_CODE } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { useAddPerson, useRemovePerson, useResolvePersonRequest, workersQuery } from '../../lib/tasks-api';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { IconButton } from '../ui/icon-button';
import { Popover } from '../ui/popover';
import { Skeleton } from '../ui/skeleton';
import { textLinkClass } from '../ui/text-link';
import { ToolbarSearch } from '../ui/toolbar-controls';
import { useToast } from '../ui/toast';

type Team = TaskDetail['team'][number];

const loadText = (w: Pick<Team, 'activeTaskCount' | 'maxActiveTasks'>) =>
  `${w.activeTaskCount} of ${w.maxActiveTasks} tasks`;

/** "Lead · Social pod · 3 of 5 tasks" for a lead, "Designer · 5 of 8 tasks" for everyone else (Figma A05.4). */
const subtitle = (w: Pick<Team, 'staffRole' | 'title' | 'department' | 'activeTaskCount' | 'maxActiveTasks'>) =>
  [
    w.staffRole === 'lead_specialist' ? 'Lead' : w.title,
    w.staffRole === 'lead_specialist' ? w.department : null,
    loadText(w),
  ]
    .filter(Boolean)
    .join(' · ');

function Person({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-(--mbm-space-3)">
      <Avatar tone="neutral" size="md" initials={initialsOf(name)} />
      {children}
    </div>
  );
}

/** Why an add failed, in words for the person doing it. */
const addErrorText = (err: unknown) => {
  const e = err as Partial<MappedApiError>;
  if (e.kind === 'forbidden') return 'You can’t add people to this task.';
  if (e.kind === 'not_found') return 'This task is no longer available.';
  if (e.status === 409 || e.status === 422)
    return e.fieldErrors?.[0]?.message ?? e.message ?? 'That can’t be done right now.';
  return 'We couldn’t do that. Please try again.';
};

/** The label over a group of people in the picker: "ON THIS TASK", "OTHER PODS". */
function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="pb-(--mbm-space-0-5) pl-(--mbm-space-2) pt-(--mbm-space-2) text-label-tag font-bold tracking-wide text-text-tertiary uppercase">
      {children}
    </h3>
  );
}

function PickRow({
  name,
  line,
  right,
  checked,
  onToggle,
}: {
  name: string;
  line: string;
  right?: React.ReactNode;
  /** `undefined` = not selectable here (already on the task). */
  checked?: boolean;
  onToggle?: () => void;
}) {
  const selectable = checked !== undefined;
  return (
    <label
      className={cx(
        'flex items-center gap-(--mbm-space-2-5) rounded-(--mbm-component-radius-rail-item) p-(--mbm-space-2)',
        selectable ? 'cursor-pointer' : 'cursor-default',
        checked && 'bg-surface-muted',
      )}
    >
      <Avatar tone="neutral" size="sm" initials={initialsOf(name)} />
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="text-label-medium text-text-primary">{name}</span>
        <span className="text-body-xsmall text-text-tertiary">{line}</span>
      </span>
      {right}
      {selectable ? (
        <>
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            aria-label={`Select ${name}`}
            className="peer sr-only"
          />
          <span
            aria-hidden
            className={cx(
              'flex size-(--mbm-size-checkbox) shrink-0 items-center justify-center rounded-(--mbm-component-radius-checkbox) border-[1.5px] text-text-on-dark',
              'peer-checked:border-action-primary peer-checked:bg-action-primary border-border-strong',
              'peer-focus-visible:shadow-(--mbm-shadow-focus-ring) [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100',
            )}
          >
            <Icon icon={Check} size="sm" />
          </span>
        </>
      ) : null}
    </label>
  );
}

/**
 * Figma A05.4d "Add people": a popover over the Team card. Search, then tick as many as you like: the ones already
 * on the task, the people in the task's own pod first, then everyone else. An account manager or admin adds them
 * straight on; a lead only suggests, and an admin approves. Someone at capacity needs a second, explicit yes.
 */
export interface AddResult {
  /** The people who were added (or suggested). */
  added: string[];
  /** Those who are at capacity and need a second yes. */
  atCapacity: Worker[];
  error?: string;
}

/** Who the picker shows as already on the task, and how it adds more: the task page and the new-task form share it. */
export interface PeoplePickerProps {
  team: Pick<Team, 'id' | 'name' | 'staffRole' | 'title' | 'department' | 'activeTaskCount' | 'maxActiveTasks'>[];
  /** People a lead has suggested that are waiting for an admin. */
  suggestedIds?: string[];
  /** A lead only suggests: the wording changes, and nothing happens until an admin approves. */
  suggests?: boolean;
  onAdd: (people: Worker[], confirmOverCapacity: boolean) => Promise<AddResult>;
  /** The text of the link that opens the popover. */
  label?: string;
}

export function PeoplePicker({ team, suggestedIds = [], suggests = false, onAdd, label }: PeoplePickerProps) {
  const [text, setText] = useState('');
  const q = useDebouncedValue(text.trim(), 250);
  const workers = useQuery(workersQuery(q));
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const [atCapacity, setAtCapacity] = useState<Worker[]>([]);

  const onTask = new Set(team.map((m) => m.id));
  const suggested = new Set(suggestedIds);
  // The task's pod is its lead's, or failing that the first person's: those people are offered first.
  const pod = (team.find((m) => m.staffRole === 'lead_specialist') ?? team[0])?.department ?? null;
  const people = workers.data ?? [];
  const available = people.filter((w) => !onTask.has(w.id));
  const inPod = pod ? available.filter((w) => w.department === pod) : available;
  const others = pod ? available.filter((w) => w.department !== pod) : [];
  const here = team.filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const run = async (workersToAdd: Worker[], confirmOverCapacity: boolean) => {
    setError(undefined);
    setBusy(true);
    try {
      const result = await onAdd(workersToAdd, confirmOverCapacity);
      setPicked((p) => p.filter((id) => !result.added.includes(id)));
      setAtCapacity(result.atCapacity);
      setError(result.error);
    } finally {
      setBusy(false);
    }
  };

  const commit = () =>
    void run(
      available.filter((w) => picked.includes(w.id)),
      false,
    );
  const line = (w: Pick<Team, 'title' | 'department' | 'activeTaskCount' | 'maxActiveTasks' | 'staffRole'>) =>
    [
      w.title ?? (w.staffRole === 'lead_specialist' ? 'Lead' : 'Specialist'),
      w.department !== pod ? w.department : null,
      loadText(w),
    ]
      .filter(Boolean)
      .join(' · ');
  const full = (w: Pick<Worker, 'activeTaskCount' | 'maxActiveTasks'>) => w.activeTaskCount >= w.maxActiveTasks;
  const row = (w: Worker) => (
    <PickRow
      key={w.id}
      name={w.name}
      line={line(w)}
      checked={suggested.has(w.id) ? undefined : picked.includes(w.id)}
      onToggle={() => toggle(w.id)}
      right={
        suggested.has(w.id) ? (
          <span className="text-label-small text-text-tertiary">Suggested</span>
        ) : full(w) ? (
          <span className="text-label-small text-tag-amber-text">At capacity</span>
        ) : undefined
      }
    />
  );

  return (
    <>
      <Popover
        placement="bottom-end"
        className="w-(--mbm-layout-picker-popover-width) p-(--mbm-space-3)"
        trigger={(props) => (
          <button type="button" {...props} className={textLinkClass}>
            {label ?? (suggests ? 'Suggest someone' : 'Add people')}
          </button>
        )}
      >
        {() => (
          <div className="flex flex-col gap-(--mbm-space-1-5)">
            {error ? <Banner tone="error">{error}</Banner> : null}
            <ToolbarSearch
              filled
              aria-label="Search staff"
              placeholder="Search staff by name or skill"
              value={text}
              onChange={setText}
              className="w-full"
            />
            <div className="max-h-(--mbm-layout-picker-list-max-height) overflow-y-auto">
              {here.length > 0 ? (
                <>
                  <GroupLabel>On this task</GroupLabel>
                  {here.map((m) => (
                    <PickRow
                      key={m.id}
                      name={m.name}
                      line={line(m)}
                      right={<span className="text-label-small text-text-tertiary">On task</span>}
                    />
                  ))}
                </>
              ) : null}
              {workers.isLoading ? (
                <Skeleton className="mt-(--mbm-space-2) h-(--mbm-space-9) w-full" />
              ) : workers.isError ? (
                <Banner tone="error">We couldn’t load the team.</Banner>
              ) : (
                <>
                  {inPod.length > 0 ? (
                    <>
                      <GroupLabel>{pod ? `Suggested · ${pod}` : 'People'}</GroupLabel>
                      {inPod.map(row)}
                    </>
                  ) : null}
                  {others.length > 0 ? (
                    <>
                      <GroupLabel>Other pods</GroupLabel>
                      {others.map(row)}
                    </>
                  ) : null}
                  {available.length === 0 ? (
                    <p className="py-(--mbm-space-4) text-center text-body-small text-text-secondary">
                      No one matches.
                    </p>
                  ) : null}
                </>
              )}
            </div>
            <div className="flex items-center gap-(--mbm-space-2-5) border-t border-border-line pl-(--mbm-space-2) pt-(--mbm-space-1-5)">
              <p className="flex-1 text-body-xsmall text-text-tertiary">
                {suggests ? 'An admin approves suggestions.' : 'They join right away.'}
              </p>
              <Button size="small" disabled={picked.length === 0} loading={busy} onClick={commit}>
                {suggests ? 'Suggest' : 'Add'} {picked.length || ''} {picked.length === 1 ? 'person' : 'people'}
              </Button>
            </div>
          </div>
        )}
      </Popover>
      <ConfirmDialog
        open={atCapacity.length > 0}
        title={
          atCapacity.length === 1
            ? `${atCapacity[0]!.name} is at capacity`
            : `${atCapacity.length} people are at capacity`
        }
        description={`${atCapacity.map((w) => `${w.name} (${loadText(w)})`).join(', ')}. Adding them anyway puts them over it.`}
        confirmLabel="Add anyway"
        loading={busy}
        onCancel={() => setAtCapacity([])}
        onConfirm={() => void run(atCapacity, true)}
      />
    </>
  );
}

/** The task page's picker: adds (or, for a lead, suggests) each person with its own request, so one failure stops no one else. */
function TaskPeoplePicker({ task, suggests }: { task: TaskDetail; suggests: boolean }) {
  const toast = useToast();
  const add = useAddPerson(task.id);

  const onAdd = async (people: Worker[], confirmOverCapacity: boolean): Promise<AddResult> => {
    const result: AddResult = { added: [], atCapacity: [] };
    for (const worker of people) {
      try {
        await add.mutateAsync({ staffId: worker.id, confirmOverCapacity });
        result.added.push(worker.id);
      } catch (err) {
        if ((err as Partial<MappedApiError>).code === OVER_CAPACITY_CODE) result.atCapacity.push(worker);
        else result.error = addErrorText(err);
      }
    }
    const n = result.added.length;
    if (n > 0) {
      toast({
        title: suggests ? 'Suggestion sent' : n === 1 ? 'Person added' : `${n} people added`,
        description: suggests ? 'An admin will approve it.' : undefined,
      });
    }
    return result;
  };

  return (
    <PeoplePicker
      team={task.team}
      suggestedIds={task.pendingRequests.map((r) => r.candidate.id)}
      suggests={suggests}
      onAdd={onAdd}
    />
  );
}

export interface TeamPanelProps {
  task: TaskDetail;
  /** The caller is a lead on this task: they can only suggest people. */
  canSuggest: boolean;
}

/** Figma A05.4 "Team": who is on the task, "Add people", and a lead's suggestion waiting for an admin. */
export function TeamPanel({ task, canSuggest }: TeamPanelProps) {
  const toast = useToast();
  const remove = useRemovePerson(task.id);
  const resolve = useResolvePersonRequest(task.id);
  const [overCapacity, setOverCapacity] = useState<{ requestId: string; name: string; message: string } | null>(null);
  const canAdd = task.access.manage || canSuggest;
  const closed = task.status === 'Cancelled' || task.status === 'User Draft';

  const fail = (err: unknown) =>
    toast({ title: 'Couldn’t update the team', description: addErrorText(err), tone: 'error' });

  const answer = (requestId: string, name: string, approve: boolean, confirmOverCapacity = false) =>
    resolve.mutate(
      { requestId, approve, confirmOverCapacity },
      {
        onSuccess: () => {
          setOverCapacity(null);
          toast({ title: approve ? `${name} added` : 'Suggestion declined' });
        },
        onError: (err) => {
          if ((err as Partial<MappedApiError>).code === OVER_CAPACITY_CODE) {
            setOverCapacity({ requestId, name, message: (err as Partial<MappedApiError>).message ?? '' });
          } else fail(err);
        },
      },
    );

  return (
    <Card tone="panel" className="flex flex-col gap-(--mbm-space-4)">
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h3 text-text-primary">Team</h2>
        {canAdd && !closed ? <TaskPeoplePicker task={task} suggests={!task.access.manage} /> : null}
      </div>

      {task.team.length === 0 ? (
        <p className="text-body-medium text-text-secondary">Nobody is on this task yet.</p>
      ) : (
        <ul className="flex flex-col gap-(--mbm-space-3)">
          {task.team.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-(--mbm-space-3)">
              <Person name={m.name}>
                <div className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-(--mbm-space-1-5) text-label-large text-text-primary">
                    {m.name}
                    {m.staffRole === 'lead_specialist' ? <Icon icon={Crown} size="sm" /> : null}
                  </span>
                  <span className="text-body-small text-text-secondary">{subtitle(m)}</span>
                </div>
              </Person>
              {task.access.manage && !closed ? (
                <IconButton
                  icon={X}
                  variant="ghost"
                  iconSize="sm"
                  aria-label={`Remove ${m.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(m.id, { onError: fail })}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {task.pendingRequests.map((r) => (
        <div
          key={r.id}
          className="flex flex-col gap-(--mbm-space-3) rounded-(--mbm-radius-md) border border-border-default p-(--mbm-space-3)"
        >
          <Person name={r.candidate.name}>
            <div className="flex min-w-0 flex-col">
              <span className="text-label-large text-text-primary">{r.candidate.name}</span>
              <span className="text-body-small text-text-secondary">
                Added by {r.requestedBy.name} · waiting for {task.access.approve ? 'your' : 'an admin’s'} approval
              </span>
            </div>
          </Person>
          {task.access.approve ? (
            <div className="grid grid-cols-2 gap-(--mbm-space-2)">
              <Button
                variant="secondary"
                size="small"
                disabled={resolve.isPending}
                onClick={() => answer(r.id, r.candidate.name, false)}
              >
                Decline
              </Button>
              <Button size="small" loading={resolve.isPending} onClick={() => answer(r.id, r.candidate.name, true)}>
                Approve
              </Button>
            </div>
          ) : null}
        </div>
      ))}

      <ConfirmDialog
        open={!!overCapacity}
        title={`${overCapacity?.name ?? 'This person'} is at capacity`}
        description={`${overCapacity?.message ?? ''} Approving puts them over it.`}
        confirmLabel="Approve anyway"
        loading={resolve.isPending}
        onCancel={() => setOverCapacity(null)}
        onConfirm={() => overCapacity && answer(overCapacity.requestId, overCapacity.name, true, true)}
      />
    </Card>
  );
}
